import React from 'react';
import {
  ArrowDownLeft,
  ArrowUpRight,
  FileSignature,
  Loader2,
  PenLine,
  RefreshCw,
  ShieldAlert,
  Trash2,
  UserRound,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import { formatRial } from '@/lib/formatMoney';
import { amountToPersianWordsToman } from '@/lib/rialWordsFa';
import {
  bsiInternetBankApi,
  type BsiIbPendingTransfer,
  type BsiIbTransferPreview,
} from '@/lib/banks/bsi/api';
import { isSessionExpiredMessage, type BsiFlashMessage } from './helpers';

type Props = {
  sessionValid: boolean;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
};

type UiStep = 'list' | 'sign' | 'done';
type ChallengeType = 'sms' | 'signature_password' | string;

function beneficiaryLabel(row: BsiIbPendingTransfer): string {
  const name = (row.to_name || '').trim();
  if (name) return name;
  const desc = (row.description || '').trim();
  if (desc) return desc;
  return 'گیرنده نامشخص';
}

function destinationAccountLabel(row: BsiIbPendingTransfer): string {
  const iban = (row.to_iban || '').trim();
  if (iban) return iban;
  return row.to_account || '—';
}

const BsiPendingTab: React.FC<Props> = ({ sessionValid, onMessage, onSessionExpired }) => {
  const [step, setStep] = React.useState<UiStep>('list');
  const [loading, setLoading] = React.useState(false);
  const [signing, setSigning] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [canceling, setCanceling] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<BsiIbPendingTransfer[]>([]);
  const [cancelTarget, setCancelTarget] = React.useState<BsiIbPendingTransfer | null>(null);
  const [activeRow, setActiveRow] = React.useState<BsiIbPendingTransfer | null>(null);

  const [preview, setPreview] = React.useState<BsiIbTransferPreview | null>(null);
  const [confirmDraftKey, setConfirmDraftKey] = React.useState<string | null>(null);
  const [challengeType, setChallengeType] = React.useState<ChallengeType>('sms');
  const [challengeLabel, setChallengeLabel] = React.useState('شناسه تأیید پیامک');
  const [authStep, setAuthStep] = React.useState(1);
  const [challenge, setChallenge] = React.useState('');
  const [ackIrreversible, setAckIrreversible] = React.useState(false);
  const [reference, setReference] = React.useState<string | null>(null);

  const signableCount = items.filter((i) => i.can_sign).length;

  const loadList = React.useCallback(async () => {
    setLoading(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.pendingTransfers();
      setItems(res.data.items || []);
      setStep('list');
      setPreview(null);
      setConfirmDraftKey(null);
      setChallenge('');
      setReference(null);
      setActiveRow(null);
      onMessage({ type: 'success', text: res.message || 'لیست قابل امضا به‌روز شد.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'خطا در دریافت تراکنش‌های قابل امضا';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setLoading(false);
    }
  }, [onMessage, onSessionExpired]);

  const booted = React.useRef(false);
  React.useEffect(() => {
    if (!sessionValid || booted.current) return;
    booted.current = true;
    void loadList();
  }, [sessionValid, loadList]);

  const handleStartSign = async (row: BsiIbPendingTransfer) => {
    if (!row.can_sign) {
      onMessage({ type: 'error', text: 'این تراکنش فعلاً قابل امضا نیست.' });
      return;
    }
    if (!row.sign_supported) {
      onMessage({ type: 'error', text: 'امضای این نوع انتقال هنوز پشتیبانی نمی‌شود.' });
      return;
    }
    setSigning(true);
    setActiveRow(row);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.pendingTransferSign({
        saba_receipt: row.saba_receipt,
        transfer_type_code: row.transfer_type_code,
        to_account: row.to_account || undefined,
      });
      setPreview(res.data.preview);
      setConfirmDraftKey(res.data.confirm_draft_key);
      setChallengeType(res.data.challenge_type || 'sms');
      setChallengeLabel(res.data.challenge_label || 'شناسه تأیید پیامک');
      setAuthStep(res.data.step || 1);
      setChallenge('');
      setAckIrreversible(false);
      setStep('sign');
      onMessage({ type: 'success', text: res.message || 'صفحه امضا آماده است.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'شروع امضا ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setSigning(false);
    }
  };

  const handleCancelConfirmed = async () => {
    const row = cancelTarget;
    if (!row) return;
    setCanceling(row.saba_receipt);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.pendingTransferCancel({
        saba_receipt: row.saba_receipt,
        transfer_type_code: row.transfer_type_code,
        to_account: row.to_account || undefined,
        from_account: row.from_account || undefined,
      });
      setCancelTarget(null);
      onMessage({ type: 'success', text: res.message || 'تراکنش حذف شد.' });
      await loadList();
    } catch (e) {
      const text = e instanceof Error ? e.message : 'حذف تراکنش ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setCanceling(null);
    }
  };

  const handleConfirm = async () => {
    if (!confirmDraftKey) {
      onMessage({ type: 'error', text: 'پیش‌نویس امضا موجود نیست؛ دوباره امضا را شروع کنید.' });
      return;
    }
    if (!ackIrreversible) {
      onMessage({ type: 'error', text: 'برای ادامه باید برگشت‌ناپذیر بودن را تأیید کنید.' });
      return;
    }
    if (!challenge.trim()) {
      onMessage({
        type: 'error',
        text:
          challengeType === 'signature_password'
            ? 'رمز امضا را وارد کنید.'
            : 'شناسه تأیید پیامک را وارد کنید.',
      });
      return;
    }
    setConfirming(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.localTransferConfirm({
        confirm_draft_key: confirmDraftKey,
        challenge: challenge.trim(),
      });
      if (res.data.needs_next_step) {
        setConfirmDraftKey(res.data.confirm_draft_key);
        setChallengeType(res.data.challenge_type || 'sms');
        setChallengeLabel(res.data.challenge_label || 'کد تأیید');
        setAuthStep(res.data.step || authStep + 1);
        setChallenge('');
        onMessage({ type: 'success', text: res.message || 'مرحله بعد را تکمیل کنید.' });
        return;
      }
      setReference(res.data.reference);
      setStep('done');
      onMessage({ type: 'success', text: res.message || 'امضا ثبت شد.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'تأیید امضا ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Card className="lg:col-span-3 overflow-hidden border-slate-200 shadow-sm">
      <CardHeader className="border-b bg-gradient-to-l from-amber-50 via-white to-orange-50">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500 text-white shadow">
            <FileSignature className="h-4 w-4" />
          </span>
          تراکنش‌های قابل امضا
          {items.length > 0 ? (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
              {signableCount} مورد
            </span>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        {!sessionValid ? (
          <p className="text-sm text-amber-700">برای مشاهده و امضا، ابتدا وارد اینترنت‌بانک شوید.</p>
        ) : (
          <>
            <div className="rounded-xl border border-amber-200 bg-gradient-to-l from-amber-50 to-orange-50 px-4 py-3 text-sm text-amber-950">
              <p className="font-semibold">{signableCount} تراکنش در انتظار امضای نهایی</p>
              <p className="mt-1 text-xs text-amber-800">
                تا وقتی امضا نکنید وجه منتقل نمی‌شود. جزئیات گیرنده را قبل از امضا یا حذف بررسی کنید.
              </p>
            </div>

            {step === 'list' && (
              <>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => void loadList()} disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    <span className="mr-1">بروزرسانی لیست</span>
                  </Button>
                </div>

                {loading && items.length === 0 ? (
                  <p className="text-sm text-slate-500">در حال دریافت…</p>
                ) : items.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-600">
                    فعلاً تراکنش قابل امضایی نیست.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                    {items.map((row) => {
                      const canCancel = row.can_cancel !== false;
                      return (
                        <div
                          key={row.saba_receipt}
                          className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-amber-200 hover:shadow-md"
                        >
                          <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">
                                {row.transfer_type || `نوع ${row.transfer_type_code}`}
                              </p>
                              <p className="mt-0.5 text-xs text-slate-500">
                                {row.create_date || '—'} · رسید{' '}
                                <span className="font-mono">{row.saba_receipt}</span>
                              </p>
                            </div>
                            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-medium text-amber-800">
                              {row.pending_status || 'در انتظار امضا'}
                            </span>
                          </div>

                          <div className="mb-3 rounded-xl bg-gradient-to-l from-slate-50 to-amber-50/40 px-3 py-3">
                            <div className="flex items-start gap-2">
                              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-700">
                                <UserRound className="h-4 w-4" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] text-slate-500">واریز به</p>
                                <p className="truncate text-base font-bold text-slate-900">{beneficiaryLabel(row)}</p>
                                <p className="mt-0.5 break-all font-mono text-xs text-slate-600" dir="ltr">
                                  {destinationAccountLabel(row)}
                                </p>
                                {row.to_bank_name ? (
                                  <p className="mt-0.5 text-xs text-slate-500">{row.to_bank_name}</p>
                                ) : null}
                              </div>
                            </div>
                          </div>

                          <div className="mb-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                              <p className="flex items-center gap-1 text-[11px] text-slate-500">
                                <ArrowUpRight className="h-3.5 w-3.5" />
                                از حساب
                              </p>
                              <p className="mt-0.5 font-mono text-xs text-slate-800" dir="ltr">
                                {row.from_account || '—'}
                              </p>
                              {row.from_name ? <p className="text-xs text-slate-500">{row.from_name}</p> : null}
                            </div>
                            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                              <p className="flex items-center gap-1 text-[11px] text-slate-500">
                                <ArrowDownLeft className="h-3.5 w-3.5" />
                                مبلغ
                              </p>
                              <p className="mt-0.5 font-mono text-sm font-bold text-slate-900">
                                {formatRial(row.amount)} <span className="text-xs font-normal">ریال</span>
                              </p>
                              <p className="text-[11px] text-slate-500">{amountToPersianWordsToman(row.amount)}</p>
                            </div>
                          </div>

                          {(row.description || row.reason_desc || row.reason_code_desc) && (
                            <p className="mb-3 line-clamp-2 text-xs text-slate-600">
                              {row.description || row.reason_desc || row.reason_code_desc}
                            </p>
                          )}

                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              className="flex-1 bg-amber-600 hover:bg-amber-700 sm:flex-none"
                              disabled={signing || !row.can_sign || !row.sign_supported}
                              onClick={() => void handleStartSign(row)}
                            >
                              {signing && activeRow?.saba_receipt === row.saba_receipt ? (
                                <Loader2 className="ml-1 h-4 w-4 animate-spin" />
                              ) : (
                                <PenLine className="ml-1 h-4 w-4" />
                              )}
                              امضا / تأیید
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50 sm:flex-none"
                              disabled={canceling === row.saba_receipt || !canCancel}
                              onClick={() => setCancelTarget(row)}
                            >
                              {canceling === row.saba_receipt ? (
                                <Loader2 className="ml-1 h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="ml-1 h-4 w-4" />
                              )}
                              حذف امضا
                            </Button>
                          </div>
                          {!row.sign_supported && (
                            <p className="mt-2 text-[11px] text-amber-700">نوع انتقال هنوز برای امضا پشتیبانی نشده.</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {step === 'sign' && preview && (
              <div className="mx-auto max-w-xl space-y-4">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  مرحله {authStep} امضا
                  {challengeType === 'signature_password' ? ' — رمز امضا' : ' — شناسه تأیید پیامک'}
                </div>

                <div className="rounded-2xl border bg-slate-50 px-4 py-4 text-sm space-y-3">
                  {activeRow ? (
                    <div className="rounded-xl border border-amber-100 bg-white px-3 py-3">
                      <p className="text-[11px] text-slate-500">واریز به</p>
                      <p className="text-base font-bold text-slate-900">{beneficiaryLabel(activeRow)}</p>
                      <p className="mt-1 font-mono text-xs text-slate-600" dir="ltr">
                        {destinationAccountLabel(activeRow)}
                      </p>
                    </div>
                  ) : null}
                  {preview.saba_receipt && (
                    <p>
                      رسید: <span className="font-mono">{preview.saba_receipt}</span>
                    </p>
                  )}
                  <p>
                    از: <span className="font-mono">{preview.source_account || activeRow?.from_account || '—'}</span>
                  </p>
                  <p>
                    به: <span className="font-mono">{preview.destination_account || activeRow?.to_account || '—'}</span>
                  </p>
                  <p>
                    مبلغ:{' '}
                    <span className="font-mono font-semibold">{formatRial(preview.amount || activeRow?.amount || 0)}</span>{' '}
                    ریال
                  </p>
                  {(preview.amount || activeRow?.amount || 0) > 0 && (
                    <p className="text-xs text-slate-600">
                      {amountToPersianWordsToman(preview.amount || activeRow?.amount || 0)}
                    </p>
                  )}
                </div>

                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                  <div className="mb-1 flex items-center gap-1 font-medium">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    هشدار
                  </div>
                  امضا برگشت‌ناپذیر است و معادل تأیید نهایی تراکنش در بانک است.
                </div>

                <div className="space-y-2">
                  <Label>{challengeLabel}</Label>
                  <Input
                    type={challengeType === 'signature_password' ? 'password' : 'text'}
                    value={challenge}
                    onChange={(e) => setChallenge(e.target.value)}
                    inputMode={challengeType === 'sms' ? 'numeric' : undefined}
                    autoComplete={challengeType === 'sms' ? 'one-time-code' : 'current-password'}
                    placeholder={
                      challengeType === 'signature_password'
                        ? 'رمز امضای اینترنت‌بانک'
                        : 'کد دریافتی از پیامک بانک'
                    }
                  />
                </div>

                <label className="flex items-start gap-2 text-sm text-rose-800">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={ackIrreversible}
                    onChange={(e) => setAckIrreversible(e.target.checked)}
                  />
                  <span>جزئیات گیرنده و مبلغ را بررسی کردم و امضا را تأیید می‌کنم.</span>
                </label>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={confirming}
                    onClick={() => {
                      setStep('list');
                      setPreview(null);
                      setConfirmDraftKey(null);
                      setChallenge('');
                      setActiveRow(null);
                    }}
                  >
                    بازگشت به لیست
                  </Button>
                  <Button type="button" variant="destructive" onClick={() => void handleConfirm()} disabled={confirming}>
                    {confirming ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                    {challengeType === 'signature_password' ? 'ارسال رمز امضا' : 'تأیید با پیامک'}
                  </Button>
                </div>
              </div>
            )}

            {step === 'done' && (
              <div className="space-y-3 text-sm">
                <p className="text-emerald-700">امضا / تأیید به بانک ارسال شد.</p>
                {reference && (
                  <p>
                    شماره پیگیری: <span className="font-mono font-semibold">{reference}</span>
                  </p>
                )}
                <Button type="button" variant="outline" onClick={() => void loadList()}>
                  بازگشت به لیست قابل امضا
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
        title="حذف تراکنش قابل امضا"
        description={
          cancelTarget
            ? `آیا از حذف انتقال به «${beneficiaryLabel(cancelTarget)}» به مبلغ ${formatRial(cancelTarget.amount)} ریال مطمئن هستید؟ این کار از لیست امضای بانک حذف می‌شود.`
            : ''
        }
        confirmLabel="حذف امضا"
        cancelLabel="انصراف"
        variant="destructive"
        loading={Boolean(canceling)}
        closeOnConfirm={false}
        onConfirm={() => void handleCancelConfirmed()}
      />
    </Card>
  );
};

export default BsiPendingTab;
